/**
 * SEO landing page content for /programs/<slug>.
 *
 * Each entry maps a URL slug to the copy + metadata needed to render
 * a dedicated, indexable program page (OWZ-30). Copy is tuned to the
 * long-tail Google Search Console queries that brought the user to
 * the page; the local "Spring Hill, FL" modifier is woven into title,
 * H1, and body so search engines bind the page to local intent.
 *
 * If a hero image is null, ProgramPage falls back to the gradient
 * hero pattern shared with CustomPage.jsx so the page still looks
 * intentional before art-specific photography is uploaded.
 */

import { CDN_URL } from '@/config/cdn';

const PROGRAM_PAGES = {
  'kids-jujitsu': {
    slug: 'kids-jujitsu',
    title: 'Kids Jujitsu in Spring Hill, FL',
    h1: 'Kids Jujitsu Classes in Spring Hill, FL',
    subtitle: 'Confidence, discipline, and real self-defense — taught in a safe, appointment-only dojo.',
    metaDescription:
      'Kids Jujitsu near me in Spring Hill, FL. Real self-defense for ages 5+ in a small, appointment-only dojo. Focus & confidence. Call Hanshi Jim to start.',
    keywords:
      'kids jujitsu near me, jujitsu for kids Spring Hill FL, kids jiu-jitsu Spring Hill, kids self-defense Hernando County',
    ogImage: `${CDN_URL}/images/FB_IMG_1681692431483-28cf2d1c.jpg`,
    ageRange: 'Ages 5 and up',
    sections: [
      {
        heading: 'Who this class is for',
        body: 'Owaza\'s Kids Jujitsu program is built for children ages 5 through 12 who want real martial arts — not a daycare with belts. Whether your child is brand new to the mat or has trained somewhere else, we start with the same foundation: safe falls, body control, listening on the first cue, and respect for training partners. Parents tell us they bring their kids in for one of three reasons: focus at school, a confidence boost, or a clear, age-appropriate answer to bullying. All three live inside the same curriculum.',
      },
      {
        heading: 'What a class looks like',
        body: 'A typical kids class runs about 45 minutes. We open with a structured warm-up — rolls, falls, animal walks — then move into the day\'s technique block: a throw, a pin, a takedown defense, or a positional escape. Every technique is broken down step-by-step, drilled with a partner, then live-tested at low intensity so the child actually understands when and why the move works. We close with a short game that reinforces the day\'s lesson. Class sizes are intentionally small so every child gets hands-on correction from Hanshi Jim Rivera or a senior instructor.',
      },
      {
        heading: 'Why parents choose Owaza',
        body: 'We are a traditional Owaza Ryu Ju-Jitsu dojo, not a strip-mall belt factory. That means appointment-only training, no spectators on the mat, and a curriculum that emphasizes control over flash. Children earn rank by demonstrating technique under realistic resistance, not by attending a set number of classes. Our head instructor, Hanshi Jim Rivera, has decades of teaching experience and personally oversees every kids class. For parents new to martial arts, the first call is the easiest way to see if we are the right fit.',
      },
      {
        heading: 'Schedule and how to start',
        body: 'Kids classes run weekday evenings between 5:30 PM and 8:00 PM. Because we operate by appointment, the next step is a phone call — not a web form — so we can match your child to the right class slot and answer your questions about pricing, the intro period, and our background-check policy for adult members in the building.',
      },
    ],
    cta: {
      headline: 'Schedule your child\'s intro class',
      body: 'Call Hanshi Jim Rivera directly. Tell him your child\'s age and we will get you set up.',
    },
    courseSchema: {
      name: 'Kids Jujitsu in Spring Hill, FL',
      description:
        'Traditional Owaza Ryu Ju-Jitsu for kids ages 5 and up. Focus, respect, and real self-defense in a small, appointment-only dojo in Spring Hill, FL.',
      audience: 'Children ages 5-12',
    },
  },

  'aikido': {
    slug: 'aikido',
    title: 'Aikido Training in Spring Hill, FL',
    h1: 'Aikido Training in Spring Hill, FL',
    subtitle: 'Live, hands-on Aikido instruction rooted in traditional Owaza Ryu Ju-Jitsu — for adults serious about their craft.',
    metaDescription:
      'Adult Aikido near me in Spring Hill, FL. Classical blending, joint-locks & weapons at Owaza Martial Arts. Appointment-only, small groups. Call to book.',
    keywords:
      'aikido training near me, aikido Spring Hill FL, adult aikido Hernando County, aikido near me',
    ogImage: `${CDN_URL}/images/IMG_9624_crop-2453b78a.jpg`,
    ageRange: 'Adults and serious teens',
    sections: [
      {
        heading: 'About Aikido at Owaza',
        body: 'Aikido at Owaza Martial Arts is taught as part of a complete jiu-jitsu system, not as a stand-alone art divorced from the realities of grappling. Hanshi Jim Rivera and our senior instructors teach the classical principles — irimi, tenkan, kuzushi, and joint manipulation — alongside the live training base that Owaza Ryu Ju-Jitsu is built on. The result is an Aikido practice that feels honest: blending and redirection that actually work because they are tested against resisting partners every week.',
      },
      {
        heading: 'Who this is for',
        body: 'Most of our Aikido students are adults — typically late twenties through retirement age — who want a martial art that respects their body, deepens with study, and gives them tools for personal protection. Some come from karate, judo, or BJJ backgrounds and want to fill in the joint-lock and balance-breaking side of their game. Others come in completely new and stay for the lifelong-practice quality of the art. Mature teens are welcome with parent approval.',
      },
      {
        heading: 'What you will train',
        body: 'Class structure typically includes ukemi (safe falling and rolling), tai sabaki (body movement and entries), wrist and elbow locks (kotegaeshi, nikyo, sankyo), pinning techniques (ikkyo, yonkyo), and standing throws. We practice both empty-hand and weapons forms — bokken (wooden sword), jo (short staff), and tanto (knife) — because the underlying principles transfer cleanly across all three. Sessions move at the pace of the room; nobody is left behind, and seniors are pushed harder than newcomers.',
      },
      {
        heading: 'Schedule and intro',
        body: 'Adult classes run weekday evenings 5:30 PM to 8:00 PM. Because we operate by appointment and keep class sizes small, the right next step is a phone call — Hanshi Jim will explain our intro options, take a quick history of any martial arts background you have, and book you in.',
      },
    ],
    cta: {
      headline: 'Try a class',
      body: 'Call Hanshi Jim Rivera directly to schedule your intro Aikido session.',
    },
    courseSchema: {
      name: 'Aikido Training in Spring Hill, FL',
      description:
        'Adult Aikido instruction at Owaza Martial Arts in Spring Hill, FL. Traditional blending, joint-locking, and weapons work taught alongside a live ju-jitsu base. Appointment-only.',
      audience: 'Adults and mature teens',
    },
  },

  'karate-for-kids': {
    slug: 'karate-for-kids',
    title: 'Karate for Kids in Spring Hill, FL',
    h1: 'Karate for Kids in Spring Hill, FL',
    subtitle: 'Focus, discipline, and respect — starting as young as 4. A real dojo, not a daycare.',
    metaDescription:
      'Karate for kids in Spring Hill, FL — ages 4 and up. Real technique, not a belt factory. Small, appointment-only classes at Owaza. Call Hanshi Jim to book.',
    keywords:
      'karate 4 year old near me, karate for kids Spring Hill FL, kids karate Hernando County, karate classes near me kids',
    ogImage: `${CDN_URL}/images/FB_IMG_1681692431483-28cf2d1c.jpg`,
    ageRange: 'Ages 4 and up (parent assessment for the youngest)',
    sections: [
      {
        heading: 'Starting karate at 4 years old',
        body: 'Parents searching for karate for a 4-year-old usually need two things: a place where their child will actually be taught, and an instructor who is honest about what a 4-year-old can and cannot do. Owaza\'s approach is straightforward — we will sit with you and the child, watch how they listen, watch how they take direction, and tell you whether they are ready for our program or whether we should check back in three or six months. We have started kids as young as 4; we have also asked families to come back when the child was a little older. Either way, you will get a real answer.',
      },
      {
        heading: 'What kids learn',
        body: 'Our kids program borrows the strongest pieces of traditional Japanese karate — stances, blocks, basic strikes, kata, and a clear belt progression — and combines them with the grappling foundation of Owaza Ryu Ju-Jitsu. Practically, that means a child who trains here learns how to stand their ground, how to fall safely if they get knocked down, and how to disengage from a grab without anyone getting hurt. The curriculum is age-appropriate and built around three habits: listen first, respect everyone on the mat, and try hard even when the technique is new.',
      },
      {
        heading: 'Why "real dojo, not a daycare" matters',
        body: 'We are an appointment-only school. We do not do birthday parties, we do not have a viewing area for spectators, and we do not promote children to the next belt by attendance. That structure is intentional — it keeps the mat focused, lets us actually teach, and produces kids whose rank means something. If you are comparing us to a larger karate franchise, the simplest difference is class size: ours is small enough that the head instructor knows your child by name from week one.',
      },
      {
        heading: 'How to get started',
        body: 'Kids classes run on weekday evenings between 5:30 PM and 8:00 PM. Call Hanshi Jim Rivera directly to find the right slot, ask about pricing, and book an intro visit. Bringing the child along to the first call is fine; we will say hi.',
      },
    ],
    cta: {
      headline: 'Book your child\'s intro',
      body: 'One short phone call gets your child on the schedule. Hanshi Jim will walk you through what to expect.',
    },
    courseSchema: {
      name: 'Karate for Kids in Spring Hill, FL',
      description:
        'Karate for kids ages 4 and up in Spring Hill, FL. Small, appointment-only classes that combine traditional karate fundamentals with the grappling base of Owaza Ryu Ju-Jitsu.',
      audience: 'Children ages 4-12',
    },
  },

  'tai-chi': {
    slug: 'tai-chi',
    title: 'Tai Chi Classes in Spring Hill, FL',
    h1: 'Tai Chi Classes in Spring Hill, FL',
    subtitle: 'Flowing movement, breath, and balance — an internal-arts practice in Spring Hill, FL.',
    metaDescription:
      'Tai chi near me in Spring Hill, FL. Flowing Tai Chi, Qi Gong & gentle movement for adults & seniors. Balance, clarity & internal calm. Call to schedule.',
    keywords:
      'tai chi near me, tai chi classes Spring Hill FL, tai chi adults seniors Hernando County, tai chi near Spring Hill',
    ogImage: `${CDN_URL}/programs/tai-chi.png`,
    ageRange: 'Adults & Seniors',
    sections: [
      {
        heading: 'Tai Chi at Owaza',
        body: 'Our internal-arts sessions blend Tai Chi, Qi Gong, and gentle movement into one class — a chance to slow down, breathe, move, and build balance in the same private dojo as every Owaza program. Classes are led by Joseph Easter and Owaza\'s senior team. We\'re also proud to count Dr. Juan Otero Jr., Hanshi — Grandmaster of Okinawan Goju Goshin-Ryu with over 58 years of experience — among our senior advisors; he brings deep Tai Chi expertise to Owaza and guides students through the internal forms. No large fitness crowds, no background noise — just focused, movement-based practice.',
      },
      {
        heading: 'What Tai Chi training involves',
        body: 'Our sessions blend traditional Tai Chi slow-form practice with Qi Gong breathing and gentle mobility work — emphasizing balance, postural alignment, and the smooth transfer of weight through each movement. Students move through standing postures, joint-loosening warm-ups, and continuous-flow sequences that build coordination and internal calm over time. The emphasis is on quality of movement rather than speed or repetition, and the internal principles of relaxation, root, and intent give the practice a genuine martial arts framework — making the internal work tangible rather than abstract.',
      },
      {
        heading: 'Who this class is for',
        body: 'Tai Chi at Owaza is structured for adults and seniors who want a low-impact, high-benefit practice — people managing joint stress, working on fall prevention, seeking stress reduction, or simply drawn to a moving meditation with a real martial arts lineage behind it. No prior martial arts experience is required. Newcomers are welcome at any point in the curriculum because the slow, deliberate pace of Tai Chi means you learn by doing from the first class.',
      },
      {
        heading: 'Schedule and how to start',
        body: 'Tai Chi meets Tuesday and Thursday mornings from 8:00 to 9:00 AM, with an open-mat session every other Saturday from 10:00 to 11:00 AM. Classes are kept small so every student gets individual attention. The right first step is a phone call — Hanshi Jim Rivera and the Owaza team will answer your questions about the practice, the class times that fit your week, and what to wear and bring for a first class.',
      },
    ],
    cta: {
      headline: 'Schedule your first Tai Chi class',
      body: 'Call Owaza to connect with our team and get started.',
    },
    courseSchema: {
      name: 'Tai Chi Classes in Spring Hill, FL',
      description:
        'Traditional Tai Chi, Qi Gong, and internal-arts practice for adults and seniors in Spring Hill, FL. Led by the Owaza senior team with senior advisor Dr. Juan Otero, Hanshi. Flowing forms, balance, and internal calm. Appointment-only.',
      audience: 'Adults and Seniors',
    },
  },

  'yoga-internal-arts': {
    slug: 'yoga-internal-arts',
    title: 'Yoga & Internal Arts in Spring Hill, FL',
    h1: 'Yoga & Internal Arts',
    subtitle: 'Yoga, Tai Chi, and Qi Gong with Yoga Joe — gentle, low-impact practice in Spring Hill, FL.',
    metaDescription:
      'Yoga, Tai Chi & Qi Gong classes in Spring Hill, FL. Gentle, low-impact internal arts for adults & seniors. Tue & Thu mornings. $80/month or $15 drop-in.',
    keywords:
      'yoga Spring Hill FL, yoga classes near me, tai chi Spring Hill FL, qi gong Hernando County, yoga for seniors Spring Hill',
    ogImage: 'https://owazamartialarts.com/program-art/yoga-internal-arts.jpg',
    // The art is cream-on-cream, so keep the gradient hero and show it in the body instead.
    heroImage: null,
    art: {
      src: '/program-art/yoga-internal-arts.jpg',
      alt: 'Yoga Joe — Yoga, Tai Chi, Internal Arts at Owaza Martial Arts',
    },
    ageRange: 'Adults & Seniors',
    sections: [
      {
        heading: 'Yoga & Internal Arts at Owaza',
        body: 'Led by Joseph Easter ("Yoga Joe"), these sessions blend yoga-style stretching and mobility with Tai Chi, Qi Gong, and gentle movement into one class. The focus is balance, postural alignment, breath, and the smooth transfer of weight — a low-impact, high-benefit practice in the same private dojo as every Owaza program.',
      },
      {
        heading: 'Who this class is for',
        body: 'Adults and seniors managing joint stress, working on fall prevention, looking for stress reduction, or simply drawn to a moving meditation with a real martial arts lineage behind it. No prior experience is required, and newcomers are welcome at any point.',
      },
      {
        heading: 'Schedule',
        body: 'Tuesday and Thursday, 8:00–9:00 AM, plus an open-mat session every other Saturday, 10:00–11:00 AM.',
      },
      {
        heading: 'Pricing',
        body: 'Monthly (up to 10 classes): $80. Daily drop-in: $15 per session (mat fee).',
      },
      {
        heading: 'What to bring',
        body: 'Comfortable, loose or stretchy clothing you can move in. Sessions are practiced barefoot or in socks — just bring a yoga block and a water bottle.',
      },
    ],
    resources: {
      email: 'yogajoe@owazamartialarts.com',
      // CDN-hosted: the public site's CloudFront function does not serve .pdf.
      brochureUrl: `${CDN_URL}/docs/owaza-yoga-tai-chi.pdf`,
      brochureLabel: 'Yoga & Tai Chi brochure (PDF)',
    },
    cta: {
      headline: 'Reserve your spot',
      body: 'Call the dojo or email Yoga Joe to get started.',
    },
    courseSchema: {
      name: 'Yoga & Internal Arts in Spring Hill, FL',
      description:
        'Yoga, Tai Chi, and Qi Gong for adults and seniors in Spring Hill, FL. Balance, mobility, breath, and internal calm in a private dojo. Tuesday and Thursday mornings.',
      audience: 'Adults and Seniors',
    },
  },

  'judo': {
    slug: 'judo',
    title: 'Judo Classes in Spring Hill, FL',
    h1: 'Judo Classes in Spring Hill, FL',
    subtitle: 'Throws, takedowns, and ground control — taught the traditional way, in a serious dojo.',
    metaDescription:
      'Judo classes near me in Spring Hill, FL. Throws, newaza & randori inside a complete ju-jitsu system. Adults & teens welcome. Appointment-only. Call to schedule.',
    keywords:
      'judo classes near me, judo Spring Hill FL, adult judo Hernando County, judo near me',
    ogImage: `${CDN_URL}/images/IMG_9624_crop-2453b78a.jpg`,
    ageRange: 'Teens and adults',
    sections: [
      {
        heading: 'Judo at Owaza',
        body: 'Judo at Owaza Martial Arts is taught alongside — and inside of — our Owaza Ryu Ju-Jitsu curriculum. Students learn the classical Kodokan throwing system (hip throws, foot sweeps, shoulder throws, sacrifice throws), the breakfalls that make those throws safe to drill, and the newaza (ground work) that lets you finish what the throw started. Because we are a ju-jitsu base school, the joint-locks and pinning detail go deeper than you will find in a pure sport-judo room.',
      },
      {
        heading: 'Who trains here',
        body: 'Most of our judo students are teens and adults — some new to grappling, some cross-training from BJJ, karate, or wrestling, and some long-time judoka who want a traditional room with serious technique. We welcome you whether you want to compete, learn for self-defense, or simply enjoy the practice of a deep martial art. Walking in with zero experience is normal; we will start you on ukemi (safe falls) and build from there.',
      },
      {
        heading: 'What a class includes',
        body: 'A typical judo session: warm-up and breakfall drills, technique of the day (one throw plus the natural follow-up on the ground), partner drilling at progressive resistance, and — depending on the group — randori (live free-practice) at the end. We emphasize control. Every throw is taught with the partner\'s safety in mind first; that is how generations of Owaza students have stayed on the mat for decades without burning out. Belt promotion is by demonstrated skill, not by time served.',
      },
      {
        heading: 'Schedule and how to visit',
        body: 'Adult classes run weekday evenings, 5:30 PM to 8:00 PM. Because we are appointment-only and keep groups small, the right first step is a phone call. Hanshi Jim Rivera will ask about your background, explain our intro options, and put you on the schedule.',
      },
    ],
    cta: {
      headline: 'Start training',
      body: 'Call Hanshi Jim Rivera to schedule an intro judo class.',
    },
    courseSchema: {
      name: 'Judo Classes in Spring Hill, FL',
      description:
        'Traditional judo in Spring Hill, FL — throws, breakfalls, newaza, and randori — taught alongside the Owaza Ryu Ju-Jitsu curriculum. Appointment-only, adults and teens welcome.',
      audience: 'Teens and adults',
    },
  },
};

export const PROGRAM_SLUGS = Object.keys(PROGRAM_PAGES);

// Old slugs that should 301-style redirect to their current canonical slug.
export const PROGRAM_REDIRECTS = {
  'kids-jujutsu': 'kids-jujitsu',
};

export function getProgramPage(slug) {
  return PROGRAM_PAGES[slug] || null;
}

export default PROGRAM_PAGES;
